import React, { Fragment, useCallback, useContext, useRef, useState } from "react";
import classNames from "classnames";
import {
  ComboboxButtonProps as HeadlessComboboxButtonProps,
  Combobox as HeadlessComboBox,
  ComboboxProps as HeadlessComboBoxProps,
} from "@headlessui/react";
import {
  AnchorPropContext,
  AnchorType,
  BaseOptionValueType,
  ButtonSize,
  WithChildRenderProp,
  WithClassName,
  WithColorScheme,
  WithFloatingAnchor,
  WithHeadlessRenderProp,
  WithPortal,
} from "@ugitgud/legos/ui/shared";
import { useFloating } from "@floating-ui/react-dom";
import { size, useMergeRefs } from "@floating-ui/react";
import { useColorScheme } from "@ugitgud/legos/hooks/useColorScheme";
import { Button } from "@ugitgud/legos/ui/form/button/Button";
import { Input } from "@ugitgud/legos/ui/form/input/Input";
import { createPortal } from "react-dom";

export type ComboBoxOptionType<
  D = Record<string, string | string[] | number | number[] | null>,
  T extends BaseOptionValueType = string,
> = {
  buttonLabel?: React.ReactNode;
  group?: string;
  label: React.ReactNode;
  value: T;
  data?: D;
};

type OurComboProps = "value" | "defaultValue" | "onChange" | "nullable" | "nultiple" | "children";

export type ComboBoxProps<D = Record<string, string>, T extends BaseOptionValueType = string> = WithClassName &
  WithFloatingAnchor &
  WithColorScheme<{}, ComboBoxColorScheme> & {
    value?: ComboBoxOptionType<D, T> | ComboBoxOptionType<D, T>[];
    defaultValue?: ComboBoxOptionType<D, T> | ComboBoxOptionType<D, T>[];
    onChange?: (value: ComboBoxOptionType<D, T> | ComboBoxOptionType<D, T>[] | null) => void;
    nullable?: boolean;
    multiple?: boolean;
  } & Pick<
    HeadlessComboBoxProps<ComboBoxOptionType<D, T> | ComboBoxOptionType<D, T>[], true, false, typeof Fragment>,
    "children"
  > &
  Omit<React.ComponentProps<"div">, OurComboProps | "children">;

type ComboBoxRenderPropArg<D = Record<string, string>, T extends BaseOptionValueType = string> = {
  value: ComboBoxOptionType<D, T> | ComboBoxOptionType<D, T>[];
  open: boolean;
  disabled: boolean;
  activeIndex: number | null;
  activeOption: ComboBoxOptionType<D, T> | null;
};

export type ComboBoxContextType = AnchorPropContext &
  WithColorScheme<{}, ComboBoxColorScheme> & {
    inputRef: React.RefObject<HTMLInputElement> | null;
  };

const ComboBoxContext = React.createContext<ComboBoxContextType>({
  anchor: null,
  setReference: () => undefined,
  hasAnchorProp: false,
  inputRef: null,
});

const comboBoxSchemeClasses = {
  primary: "dark:bg-white hover:dark:bg-white",
  secondary: "dark:bg-purple-550 hover:dark:bg-accent-purple-700",
};

type ComboBoxColorScheme = keyof typeof comboBoxSchemeClasses;

export const ComboBox = React.forwardRef(function <D = Record<string, string>, T extends BaseOptionValueType = string>(
  { children, className, colorScheme, value, onChange, defaultValue, nullable, multiple, anchor, ...rest }: ComboBoxProps<D, T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const [anchorDefault, setAnchorDefault] = useState<AnchorType>(anchor);
  const inputRef = useRef<HTMLInputElement>(null);

  const contextValue: ComboBoxContextType = {
    anchor: anchor || anchorDefault,
    setReference: setAnchorDefault,
    hasAnchorProp: typeof anchor !== "undefined",
    colorScheme,
    inputRef,
  };

  const mergedRefs = useMergeRefs([ref, setAnchorDefault]);

  const finalClasses = useColorScheme({
    classes: comboBoxSchemeClasses,
    scheme: colorScheme,
    default: "secondary",
    className,
  });

  if (multiple) {
    return (
      <ComboBoxContext.Provider value={contextValue}>
        <div {...rest} ref={mergedRefs} className={finalClasses}>
          <HeadlessComboBox
            value={value as ComboBoxOptionType<D, T>[]}
            defaultValue={defaultValue as ComboBoxOptionType<D, T>[]}
            onChange={onChange as (props: ComboBoxOptionType<D, T>[]) => void}
            multiple
          >
            {children}
          </HeadlessComboBox>
        </div>
      </ComboBoxContext.Provider>
    );
  } else if (nullable) {
    return (
      <ComboBoxContext.Provider value={contextValue}>
        <div {...rest} ref={mergedRefs} className={finalClasses}>
          <HeadlessComboBox value={value} defaultValue={defaultValue} onChange={onChange} nullable>
            {children}
          </HeadlessComboBox>
        </div>
      </ComboBoxContext.Provider>
    );
  } else {
    return (
      <ComboBoxContext.Provider value={contextValue}>
        <div {...rest} ref={mergedRefs} className={finalClasses}>
          <HeadlessComboBox value={value} defaultValue={defaultValue} onChange={onChange} nullable={false}>
            {children}
          </HeadlessComboBox>
        </div>
      </ComboBoxContext.Provider>
    );
  }
});

const comboBoxButtonSchemeClasses = {
  primary: "dark:bg-white hover:dark:bg-white bg-purple-550",
  secondary: "dark:bg-purple-100 hover:dark:bg-accent-purple-700",
};

export type ComboBoxButtonProps<T extends React.ElementType, V extends string | number, D = Record<string, string>> = Omit<
  HeadlessComboboxButtonProps<T>,
  "children"
> &
  WithClassName &
  WithColorScheme &
  WithChildRenderProp<{ disabled: boolean; open: boolean; value: ComboBoxOptionType<D, V> }>;

export const ComboBoxButton = React.forwardRef(function <T extends React.ElementType, V extends string | number>(
  { children, as, colorScheme, className, ...rest }: ComboBoxButtonProps<T, V>,
  ref: React.RefObject<HTMLButtonElement> | React.ForwardedRef<HTMLButtonElement>,
) {
  const { colorScheme: contextScheme } = useContext(ComboBoxContext);

  const finalClasses = useColorScheme({
    classes: comboBoxButtonSchemeClasses,
    scheme: colorScheme || contextScheme,
    default: "secondary",
    className: classNames(className),
    common: "flex items-center",
  });

  return (
    <HeadlessComboBox.Button {...rest} as={as} className={finalClasses} ref={ref}>
      {(props) => (typeof children === "function" ? <>{children(props)}</> : <>{children}</>)}
    </HeadlessComboBox.Button>
  );
});

const comboBoxInputSchemeClasses = {
  secondary: "placeholder:text-accent-gray-100 dark:text-lavender-50 dark:hover:bg-accent-purple-700",
  primary: "placeholder:text-accent-gray-300 text-lavender-400 dark:bg-white",
};

type OurComboInputProps = {
  value?: string;
  displayValue?: (value?: ComboBoxOptionType) => string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  setInputValue?: (value: string) => void;
  onSubmit?: (e: React.FormEvent) => void;
  placeholder?: string;
};

export type ComboBoxInputProps = OurComboInputProps &
  WithClassName &
  Omit<React.ComponentProps<typeof Input>, keyof OurComboInputProps | "colorScheme"> &
  WithColorScheme<{}, "primary" | "secondary">;

export const ComboBoxInput = React.forwardRef<HTMLInputElement, React.PropsWithChildren<ComboBoxInputProps>>(
  ({ colorScheme, value, onSubmit, onChange, displayValue, className, placeholder, ...rest }, ref) => {
    const { colorScheme: contextScheme, inputRef } = useContext(ComboBoxContext);
    const finalClasses = useColorScheme({
      classes: comboBoxInputSchemeClasses,
      scheme: colorScheme || contextScheme,
      default: "secondary",
      common: "bg-transparent h-full font-normal outline-none text-lg text-ellipsis overflow-hidden",
      className,
    });

    const mergedRefs = useMergeRefs([ref, inputRef]);

    return (
      <HeadlessComboBox.Input
        {...rest}
        as={Input}
        ref={mergedRefs}
        placeholder={placeholder}
        className={finalClasses}
        value={value}
        onChange={onChange}
        onSubmit={onSubmit}
      />
    );
  },
);

const comboBoxOptionsSchemeClasses = {
  primary: "dark:bg-white dark:text-accent-gray-200",
  secondary: "dark:bg-purple-550 dark:text-lavender-50",
};

export type ComboBoxOptionsProps = WithClassName &
  WithColorScheme<{}, ComboBoxColorScheme> &
  WithChildRenderProp<{ open: boolean }> &
  WithPortal & {
    autoSize?: boolean;
  };

export const ComboBoxOptions = React.forwardRef<HTMLUListElement, ComboBoxOptionsProps>(
  ({ children, colorScheme, autoSize = true, className, portal }, ref: React.ForwardedRef<HTMLUListElement>) => {
    const { colorScheme: contextScheme, anchor } = useContext(ComboBoxContext);
    const { refs, floatingStyles } = useFloating({
      placement: "bottom-start",
      elements: {
        reference: anchor,
      },
      strategy: "absolute",
      middleware: [
        size({
          apply: ({ elements }) => {
            Object.assign(elements.floating.style, {
              "min-width": `${elements.reference.getBoundingClientRect().width}px`,
            });
          },
        }),
      ],
    });
    const mergedRefs = useMergeRefs([ref, refs.setFloating]);
    const finalClasses = useColorScheme({
      classes: comboBoxOptionsSchemeClasses,
      scheme: colorScheme || contextScheme,
      default: "secondary",
      common: "absolute z-[999] w-max rounded-sm mt-2 max-h-[300px] overflow-y-auto",
      className,
    });

    const content = (
      <HeadlessComboBox.Options className={finalClasses} style={floatingStyles} ref={mergedRefs}>
        {typeof children === "function" ? (props) => children(props) : children}
      </HeadlessComboBox.Options>
    );

    return portal ? createPortal(content, portal) : content;
  },
);

type ComboBoxOptionProps = {
  option: ComboBoxOptionType;
} & WithClassName &
  WithColorScheme &
  WithHeadlessRenderProp<{ active: boolean; selected: boolean; disabled: boolean }>;

const comboBoxOptionSchemeClasses = {
  primary: "dark:bg-white hover:dark:bg-accent-gray-50 ui-active:dark:bg-accent-gray-50",
  secondary: "dark:bg-purple-200 hover:dark:bg-lavender-500 ui-active:dark:bg-lavender-500 hover:font-bold",
};

export const ComboBoxOption = React.forwardRef<HTMLLIElement, ComboBoxOptionProps>(
  ({ children, option, colorScheme, className }, ref: React.ForwardedRef<HTMLLIElement>) => {
    const { colorScheme: contextScheme } = useContext(ComboBoxContext);
    const finalClasses = useColorScheme({
      classes: comboBoxOptionSchemeClasses,
      scheme: colorScheme || contextScheme,
      default: "secondary",
      common: "flex flex-row gap-3 px-3 py-1.5 text-sm cursor-pointer",
      className,
    });
    return (
      <HeadlessComboBox.Option key={option.value} value={option} className={finalClasses} ref={ref}>
        {({ active, selected, disabled }) =>
          typeof children === "function" ? children({ active, selected, disabled }) : children
        }
      </HeadlessComboBox.Option>
    );
  },
);

export type ComboBoxButtonInputLabelProps<V extends string | number, D = Record<string, string>> = {
  value?: ComboBoxOptionType<D, V> | ComboBoxOptionType<D, V>[];
  inputValue?: string;
  onInputChange: React.ChangeEventHandler;
  onInputBlur?: React.EventHandler<React.FocusEvent>;
  showInput: boolean;
  placeholder?: string;
};

export const ComboBoxButtonInputLabel = React.forwardRef(
  <V extends string | number, D = Record<string, string>>(
    props: ComboBoxButtonInputLabelProps<V, D>,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const { value, inputValue, onInputChange, onInputBlur, showInput, placeholder } = props;
    const handleRef = useCallback((ref: HTMLInputElement | null) => {
      ref?.focus();
    }, []);
    const mergedRefs = useMergeRefs([handleRef, ref]);
    return showInput ? (
      <ComboBoxInput
        value={inputValue}
        onChange={onInputChange}
        onBlur={onInputBlur}
        placeholder={placeholder}
        className="flex w-full text-[length:inherit] dark:bg-transparent dark:hover:bg-transparent p-0 grow"
        ref={mergedRefs}
      />
    ) : (
      <>
        {Array.isArray(value)
          ? value.map((x) => <React.Fragment key={x.value}>{x.buttonLabel}</React.Fragment>)
          : value?.buttonLabel || value?.label || null}
      </>
    );
  },
);
